import type {
  AppLayoutNavigation,
  SidebarNavigation,
} from "@app/components/navigation/config";
import { matchesRoutePattern } from "@app/components/navigation/config";
import {
  useSetContentWidth,
  useSetSubNavigation,
} from "@app/components/sparkle/AppLayoutContext";
import { useWorkspace } from "@app/lib/auth/AuthContext";
import { useAppRouter } from "@app/lib/platform";
import {
  BarChart01,
  Cloud01,
  Dataflow02,
  List,
  ShieldTick,
} from "@dust-tt/sparkle";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

// The Knowledge section's left sub-nav. Mirrors AdminLayout: build a
// SidebarNavigation[] and register it via useSetSubNavigation; the shared
// NavigationSidebar renders it (gated by the Knowledge top-nav tab's isCurrent,
// which matches /w/[wId]/knowledge[/sub]). Renders <Outlet/> for the active
// child section.
//
// The menu ids (graph/facts/verify/coverage) are outside the shared
// AppLayoutNavigation.id union (used only as React keys by the renderer), so the
// nav is asserted to SidebarNavigation[] here rather than widening the shared
// types in navigation/config.ts.
export function KnowledgeRouterLayout() {
  const { t } = useTranslation();
  const owner = useWorkspace();
  const router = useAppRouter();
  const currentRoute = router.pathname;

  const subNavigation = useMemo<SidebarNavigation[]>(() => {
    const base = `/w/${owner.sId}/knowledge`;
    const menus = [
      {
        id: "knowledge",
        label: t("knowledge.navOverview", "Overview"),
        icon: BarChart01,
        href: base,
        current: matchesRoutePattern(currentRoute, ["/w/[wId]/knowledge"]),
      },
      // 검증 comes right after 개요: reviewing proposed facts is the action the
      // whole tab exists for — browse views (사실/그래프) follow, and the
      // sources rollup closes the list.
      {
        id: "verify",
        label: t("knowledge.navVerify", "Verify"),
        icon: ShieldTick,
        href: `${base}/verify`,
        current: matchesRoutePattern(currentRoute, [
          "/w/[wId]/knowledge/verify",
        ]),
      },
      {
        id: "facts",
        label: t("knowledge.navFacts", "Facts"),
        icon: List,
        href: `${base}/facts`,
        current: matchesRoutePattern(currentRoute, [
          "/w/[wId]/knowledge/facts",
        ]),
      },
      {
        id: "graph",
        label: t("knowledge.navGraph", "Graph"),
        icon: Dataflow02,
        href: `${base}/graph`,
        current: matchesRoutePattern(currentRoute, [
          "/w/[wId]/knowledge/graph",
        ]),
      },
      {
        id: "coverage",
        label: t("knowledge.navCoverage", "Sources"),
        icon: Cloud01,
        href: `${base}/coverage`,
        current: matchesRoutePattern(currentRoute, [
          "/w/[wId]/knowledge/coverage",
        ]),
      },
    ] as unknown as AppLayoutNavigation[];

    return [
      {
        id: "workspace",
        label: t("knowledge.navSectionLabel", "Knowledge"),
        menus,
      },
    ];
  }, [owner.sId, currentRoute, t]);

  useSetSubNavigation(subNavigation);
  // Use the standard full-width content frame (px-4 md:px-8 + pt-8) like every
  // other Dust page. Without this the pages render flush against the panel
  // edges with no margin.
  useSetContentWidth("wide");

  return <Outlet />;
}
